import React from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { User, Package, Heart, LogOut, MessageSquareText, LayoutDashboard } from "lucide-react";
import { SEO } from "@/components/common/SEO";
import { Breadcrumbs } from "@/components/common/Breadcrumbs";
import { useAuth } from "@/context/AuthContext";
import { useWishlist } from "@/context/WishlistContext";
import { ShopifyConnectionCard } from "@/components/account/ShopifyConnectionCard";
import { ReviewModerationPanel } from "@/components/account/ReviewModerationPanel";
import { useAdminReviews } from "@/hooks/useReviews";

const tabClass = "shrink-0 rounded-none border-b-2 border-transparent px-3 py-3 text-xs uppercase tracking-wider data-[state=active]:border-gold data-[state=active]:bg-transparent data-[state=active]:text-gold data-[state=active]:shadow-none sm:px-4";

function ReviewsTabTrigger() {
  const { data } = useAdminReviews({ status: "pending", limit: 1 });
  const pending = data?.counts?.pending || 0;
  return (
    <TabsTrigger value="reviews" className={tabClass} data-testid="account-tab-reviews">
      <MessageSquareText size={15} className="mr-2" /> Reviews
      {pending > 0 && <span className="ml-2 bg-amber-100 px-1.5 py-0.5 text-[10px] text-amber-800" data-testid="account-pending-reviews-badge">{pending}</span>}
    </TabsTrigger>
  );
}

export default function AccountPage() {
  const { user, logout } = useAuth();
  const { count } = useWishlist();
  const navigate = useNavigate();

  if (user === null) {
    return <div className="flex min-h-[50vh] items-center justify-center text-sm text-muted-foreground">Loading your account…</div>;
  }
  if (user === false) {
    return <Navigate to="/login" replace />;
  }

  const initials = (user.name || user.email || "?").trim().charAt(0).toUpperCase();
  const isAdmin = user.role === "admin";

  return (
    <div className="mx-auto max-w-4xl px-4 py-8 sm:px-6 lg:px-8 lg:py-12">
      <SEO title="My Account" />
      <Breadcrumbs items={[{ label: "Home", to: "/" }, { label: "Account" }]} />

      <div className="mt-8 flex items-center gap-4">
        <span className="grid h-16 w-16 place-items-center rounded-full bg-gold-light font-serif text-2xl text-gold">{initials}</span>
        <div>
          <h1 className="font-serif text-3xl font-light text-ink">Hello, {user.name?.split(" ")[0] || "there"}</h1>
          <p className="text-sm text-muted-foreground">{user.email}</p>
        </div>
      </div>

      <Tabs defaultValue="overview" className="mt-10">
        <TabsList className="flex w-full justify-start gap-1 overflow-x-auto rounded-none border-b border-border bg-transparent p-0">
          <TabsTrigger value="overview" className={tabClass}>
            <User size={15} className="mr-2" /> Overview
          </TabsTrigger>
          <TabsTrigger value="orders" className={tabClass}>
            <Package size={15} className="mr-2" /> Orders
          </TabsTrigger>
          {isAdmin && <ReviewsTabTrigger />}
        </TabsList>

        <TabsContent value="overview" className="pt-8">
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
            <div className="border border-border bg-white/60 p-6">
              <h3 className="text-luxe text-[11px] text-ink">Profile Details</h3>
              <dl className="mt-4 space-y-2 text-sm">
                <div className="flex justify-between"><dt className="text-muted-foreground">Name</dt><dd className="text-ink">{user.name}</dd></div>
                <div className="flex justify-between"><dt className="text-muted-foreground">Email</dt><dd className="text-ink">{user.email}</dd></div>
                <div className="flex justify-between"><dt className="text-muted-foreground">Account</dt><dd className="text-ink capitalize">{user.role}</dd></div>
              </dl>
            </div>
            <button onClick={() => navigate("/wishlist")} className="flex flex-col items-start border border-border bg-white/60 p-6 text-left transition-colors hover:border-gold">
              <Heart size={20} className="text-gold" />
              <h3 className="mt-4 font-serif text-xl text-ink">My Wishlist</h3>
              <p className="mt-1 text-sm text-muted-foreground">{count} saved {count === 1 ? "piece" : "pieces"}</p>
            </button>
            {isAdmin && (
              <Link to="/admin" data-testid="account-open-admin" className="group border border-ink bg-ink p-6 text-left text-ivory transition-colors hover:bg-gold hover:text-ink">
                <LayoutDashboard size={22} className="text-gold transition-colors group-hover:text-ink" />
                <h3 className="mt-4 font-serif text-xl">Admin Dashboard</h3>
                <p className="mt-1 text-sm opacity-70">Stats, store connection and review moderation</p>
              </Link>
            )}
            {isAdmin && <ShopifyConnectionCard />}
          </div>

          <button onClick={async () => { await logout(); navigate("/"); }} className="mt-8 inline-flex items-center gap-2 border border-border px-6 py-3 text-xs uppercase tracking-widest text-ink transition-colors hover:border-destructive hover:text-destructive" data-testid="logout-button">
            <LogOut size={15} /> Sign Out
          </button>
        </TabsContent>

        <TabsContent value="orders" className="pt-8">
          <div className="flex flex-col items-center justify-center border border-dashed border-border py-20 text-center">
            <Package className="h-10 w-10 text-gold" strokeWidth={1.2} />
            <p className="mt-4 font-serif text-2xl text-ink">No orders yet</p>
            <p className="mt-2 max-w-sm text-sm text-muted-foreground">
              When you place an order it will appear here. Order history syncs automatically once your Shopify store is connected.
            </p>
            <Link to="/shop" className="mt-6 bg-ink px-8 py-3.5 text-[11px] uppercase tracking-[0.2em] text-ivory hover:bg-gold">Start Shopping</Link>
          </div>
        </TabsContent>

        {isAdmin && (
          <TabsContent value="reviews" className="pt-8">
            <ReviewModerationPanel />
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}
